import React, { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { Basemap } from '../../lib/types';

interface BasemapZoomClampProps {
  basemap: Basemap;
  fallbackMinZoom: number;
  fallbackMaxZoom: number;
  /**
   * Optional additional cap from the active data layer — e.g. the canopy
   * height raster has a native max zoom of 15, so we don't want the user
   * zooming past that into upscaled tiles. When set, the effective max is
   * the minimum of this and the basemap's own max.
   */
  layerMaxZoom?: number | null;
  /**
   * When true, the basemap's own `maxZoom` is ignored and only `layerMaxZoom`
   * (or `fallbackMaxZoom`) caps zoom-in. Set this when the visible basemap
   * inside the shed has been swapped to a non-capped tileset (e.g. AGB +
   * canopy height + in-shed Stadia swap): the outer thematic basemap
   * becomes secondary context and its blur cap no longer governs what the
   * user is actually inspecting.
   */
  suppressBasemapMax?: boolean;
}

export const BasemapZoomClamp: React.FC<BasemapZoomClampProps> = ({
  basemap,
  fallbackMinZoom,
  fallbackMaxZoom,
  layerMaxZoom,
  suppressBasemapMax,
}) => {
  const map = useMap();

  useEffect(() => {
    const minZ = basemap.minZoom ?? fallbackMinZoom;
    const basemapMax = suppressBasemapMax
      ? fallbackMaxZoom
      : basemap.maxZoom ?? fallbackMaxZoom;
    const maxZ =
      layerMaxZoom != null ? Math.min(basemapMax, layerMaxZoom) : basemapMax;
    map.setMinZoom(minZ);
    map.setMaxZoom(maxZ);
    // Leaflet's setMinZoom/setMaxZoom don't clamp the current view, so do it
    // here. Snap instead of animate so downstream map-ready effects (marker
    // clustering, flyToBounds) don't compose on top of an in-flight zoom.
    const z = map.getZoom();
    if (z < minZ) map.setZoom(minZ, { animate: false });
    else if (z > maxZ) map.setZoom(maxZ, { animate: false });
  }, [
    map,
    basemap.id,
    basemap.minZoom,
    basemap.maxZoom,
    fallbackMinZoom,
    fallbackMaxZoom,
    layerMaxZoom,
    suppressBasemapMax,
  ]);

  return null;
};
